import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, CalendarRange, Info } from "lucide-react";
import { toast } from "../../../components/ui/sonner";
import { T } from "../../../lib/i18n";

/* 【2026-10-22】阶段工期编辑入口。
 *
 * 为什么单独做一个弹窗：
 *   82 个工作日这个总周期不是"手填一个数"，而是当前阶段模板工期的和。
 *   原来「产物与流程设置 → 时间」里的「设计总周期」允许直接手填，
 *   等于给同一个事实留了两个入口，填出来的数还会和阶段工期对不上（designDays 漂移）。
 *   2026-10-22 已把那个输入框改成只读，并在旁边写了"改总周期请调阶段工期"——
 *   但当时**没有这个入口**，文案指向了一个不存在的界面，等于把人指进死胡同。
 *   后端 GET/PUT /design/v3/time-cfg 早就在了，缺的只是这一层界面。
 *
 * 为什么按 group 分组而不是平铺：
 *   阶段按"前期 → Planning 申报 / 审批 → Building 申报 / 审批 →
 *   Building 审后材料 / 批准 → Permit 移交"推进，平铺一列看不出流程节奏；
 *   分组之后一眼能看出钱和时间的分布（例如两个 review 阶段各占多少天）。
 *
 * 为什么 targetMode 要暴露给主管：
 *   结束日有三种算法（baseline / stage / legacy），实现上已经写死在
 *   design_time_cfg._auto_target 里，但此前只能改库。baseline 是当前口径
 *   （签约日 + 82 工作日为标准完成日，再加延期history），stage 是按剩余阶段累加，
 *   legacy 是旧口径、只用于回滚。不给界面的话，出了问题只能连数据库。
 */

const GROUP_ORDER = ["pre", "planning", "planning_review", "building", "building_review", "building_post_review", "building_permit", "permit"];
const GROUP_LABEL = {
  pre: "前期 · Pre-Design",
  planning: "Planning 申报",
  planning_review: "Planning 审批",
  building: "Building 申报",
  building_review: "Building 审批",
  building_post_review: "Building 审后 · 材料",
  building_permit: "Building 批 + Permit",
  permit: "Permit · 移交",
};

const MODE_HINT = {
  baseline: "标准完成日 = 合同签署日 + 阶段工期合计（当前口径）。阶段里的自动延长与申请延期在标准完成日之上叠加。",
  stage: "目标完成日 = 今天 + 剩余阶段工期累加。适合中途接管、需要按剩余工作量重算的项目。",
  legacy: "旧口径，仅用于回滚历史行为。新项目不要选。",
};

export const StageDaysDialog = ({ api, onClose, onSaved }) => {
  const [stages, setStages] = useState([]);
  const [days, setDays] = useState({});
  const [mode, setMode] = useState("baseline");
  const [modes, setModes] = useState([]);
  const [isManager, setIsManager] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    setBusy(true);
    api.get("/design/v3/time-cfg")
      .then(({ data }) => {
        if (!live) return;
        const list = data.stages || [];
        setStages(list);
        setDays(Object.fromEntries(list.map((s) => [s.key, String(s.days ?? "")])));
        setMode(data.targetMode || "baseline");
        setModes(data.targetModes || []);
        setIsManager(!!data.isManager);
        setNote(data.note || "");
        setErr("");
      })
      .catch((e) => { if (live) setErr(e?.detailText || e.message); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [api]);

  const grouped = useMemo(() => {
    const acc = {};
    for (const s of stages) {
      const g = s.timeGroup || s.group || "other";
      (acc[g] = acc[g] || []).push(s);
    }
    return GROUP_ORDER.filter((g) => acc[g]?.length).map((g) => ({ key: g, label: GROUP_LABEL[g] || g, items: acc[g] }));
  }, [stages]);

  const total = useMemo(
    () => stages.reduce((n, s) => n + (Number(days[s.key]) || 0), 0),
    [stages, days],
  );
  const dirty = useMemo(
    () => stages.some((s) => String(s.days ?? "") !== String(days[s.key] ?? "")),
    [stages, days],
  );

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/design/v3/time-cfg", {
        stages: Object.fromEntries(stages.map((s) => [s.key, Number(days[s.key]) || 0])),
        targetMode: mode,
      });
      toast.success(T("阶段工期已保存，合计 {0} 个工作日").replace("{0}", String(total)));
      onSaved?.();
      onClose?.();
    } catch (e) {
      const msg = e?.detailText || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <div className="fixed inset-0 z-[86] flex items-center justify-center p-4" data-testid="dv3-stage-days-dialog">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl p-4 max-h-[88vh] flex flex-col">
        <div className="flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-amber-600" />
          <b className="text-sm text-slate-800">{T("阶段工期")}</b>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-900" data-testid="dv3-stage-days-close"><X className="h-4 w-4" /></button>
        </div>

        {busy && !stages.length && (
          <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        )}

        {!!err && !stages.length && <p className="mt-3 text-xs text-red-600" data-testid="dv3-stage-days-err">{err}</p>}

        {!!stages.length && <>
          <p className="mt-1 text-[11px] text-slate-400">
            {T("这是全公司唯一的阶段时间源：{0} 个阶段的工期之和就是设计总周期。").replace("{0}", String(stages.length))}
            {note ? ` ${note}` : ""}
          </p>
          {!isManager && (
            <p className="mt-1.5 text-[11px] text-amber-700" data-testid="dv3-stage-days-ro">
              {T("只有设计主管可以修改阶段工期；你可以查看当前配置。")}
            </p>
          )}

          <div className="mt-3 overflow-y-auto pr-0.5 flex-1 space-y-3">
            {grouped.map((g) => {
              const sub = g.items.reduce((n, s) => n + (Number(days[s.key]) || 0), 0);
              return (
                <div key={g.key}>
                  <p className="text-[11px] font-semibold text-slate-500 flex items-center gap-2">
                    {T(g.label)}
                    <span className="font-normal text-slate-400 tabular-nums">{sub} {T("个工作日")}</span>
                  </p>
                  <div className="mt-1 grid sm:grid-cols-2 gap-1.5">
                    {g.items.map((s) => (
                      <label key={s.key} className="flex items-center gap-2 text-[11px] text-slate-700 rounded-lg border border-slate-100 bg-slate-50 px-2 py-1">
                        <span className="flex-1 truncate" title={s.name}>{s.name}</span>
                        <input type="number" min={0} max={365} value={days[s.key] ?? ""} disabled={!isManager}
                          onChange={(e) => setDays((v) => ({ ...v, [s.key]: e.target.value }))}
                          className="w-14 border border-slate-200 rounded-md px-1.5 py-0.5 text-xs disabled:bg-slate-100 disabled:text-slate-400"
                          data-testid={`dv3-stage-days-${s.key}`} />
                        <span className="text-slate-400">{T("天")}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="border-t border-slate-100 pt-3">
              <p className="text-[11px] font-semibold text-slate-600">{T("结束日算法")}</p>
              <div className="mt-1 space-y-1.5">
                {(modes.length ? modes : ["baseline", "stage", "legacy"]).map((m) => (
                  <label key={m} className={`flex items-start gap-2 text-[11px] rounded-lg border px-2 py-1.5 ${mode === m ? "border-amber-300 bg-amber-50" : "border-slate-100 bg-slate-50"}`}>
                    <input type="radio" name="dv3-target-mode" value={m} checked={mode === m} disabled={!isManager}
                      onChange={() => setMode(m)} className="mt-0.5" data-testid={`dv3-stage-days-mode-${m}`} />
                    <span className="flex-1">
                      <span className="font-semibold text-slate-700">{m}</span>
                      <span className="block text-slate-500 leading-snug">{T(MODE_HINT[m] || "")}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3 pt-2 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-600">{T("合计")}</span>
            <span className="text-sm font-bold tabular-nums text-slate-800" data-testid="dv3-stage-days-total">{total}</span>
            <span className="text-xs text-slate-500">{T("个工作日")}</span>
            <span className="ml-auto inline-flex items-center gap-1">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="dv3-stage-days-cancel">{T("取消")}</button>
              <button type="button" onClick={save} disabled={!isManager || busy || !dirty}
                className="px-3 py-1.5 text-xs rounded-md bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40"
                data-testid="dv3-stage-days-save">{busy ? T("保存中…") : T("保存")}</button>
            </span>
          </div>
          {!!err && <p className="mt-1 text-[11px] text-red-600" data-testid="dv3-stage-days-err-inline">{err}</p>}
          <p className="mt-1 text-[11px] text-slate-400 flex items-start gap-1">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            {T("修改只影响之后新建的项目；已有项目的截止日已经落库，需要单独走「按新工期重排」。")}
          </p>
        </>}
      </div>
    </div>
  );

  return createPortal(body, document.body);
};

export default StageDaysDialog;
